import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useFeeTypesStore } from '../../stateManagers/store/useFeeTypesStore';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../stateManagers/newStores/useSchoolDetailsStore';

// ── Inline SVG icons — no font/CSS dependency ─────────────────────────────────
const Ico = ({ d, size = 14, style = {}, fill = false }: { d: string; size?: number; style?: React.CSSProperties; fill?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}>
    <path d={d} />
  </svg>
);

const IcoCheck       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M20 6L9 17l-5-5" />;
const IcoX           = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M18 6L6 18M6 6l12 12" />;
const IcoPlus        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M12 5v14M5 12h14" />;
const IcoPencil      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;
const IcoTrash       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const IcoSearch      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />;
const IcoRefresh     = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15" />;
const IcoUpload      = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />;
const IcoInfo        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 8v4M12 16h.01" />;
const IcoWarn        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />;
const IcoInbox       = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M22 12h-6l-2 3H10l-2-3H2M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />;
const IcoList        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />;
const IcoCheckCircle = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" />;
const IcoMinusCircle = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM8 12h8" />;
const IcoStar        = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const IcoStarFill    = (p: { size?: number; style?: React.CSSProperties }) => <Ico {...p} fill d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const IcoSpinner     = ({ size = 14, style = {} }: { size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', animation: 'spin 0.75s linear infinite', ...style }}>
    <path d="M12 2a10 10 0 0110 10" />
  </svg>
);

interface LocalFeeType {
  id: string | null;
  fee_name: string;
  status: 'active' | 'inactive';
  optional: boolean;
  isEditing: boolean;
  isDirty: boolean;
  isNew: boolean;
}

interface BulkResult {
  status: string;
  message: string;
  line?: number;
  fee_name?: string;
  fee_type_id?: string;
  fee_status?: string;
}

interface BulkEntry {
  id: string;
  fee_name: string;
  status: 'active' | 'inactive';
}

const GS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .fts-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  @keyframes fadeUp  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.6} }
  @keyframes spin    { to { transform: rotate(360deg); } }

  .fts-card {
    background: white; border-radius: 16px; padding: 24px; margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    border: 1.5px solid #e8edf5; animation: fadeUp 0.3s ease both;
  }
  .fts-card-title {
    font-size: 14px; font-weight: 700; color: #1e293b;
    margin: 0 0 18px 0; display: flex; align-items: center; gap: 10px;
  }
  .fts-icon {
    width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #2855ae, #3b82f6);
  }
  .ft-row {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px; border-radius: 12px; margin-bottom: 8px;
    border: 1.5px solid #e8edf5; background: #fafbfc;
    transition: all 0.15s; animation: slideIn 0.2s ease both;
  }
  .ft-row:hover { border-color: #bfdbfe; background: #f8fbff; }
  .ft-row.editing { border-color: #3b82f6; background: #eff6ff; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
  .ft-row.dirty   { border-color: #f59e0b; background: #fffbeb; }
  .ft-row.new-row { border-color: #22c55e; background: #f0fdf4; border-style: dashed; }
  .ft-row.inactive-row { opacity: 0.6; }
  .ft-name-input {
    flex: 1; border: none; background: transparent;
    font-size: 14px; font-weight: 600; color: #1e293b; outline: none;
    font-family: 'DM Sans', sans-serif;
  }
  .ft-name-input::placeholder { color: #94a3b8; font-weight: 400; }
  .ft-name-input:read-only { cursor: default; }
  .ft-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;
    cursor: pointer; user-select: none; transition: all 0.15s;
  }
  .ft-badge.active      { background: #dcfce7; color: #15803d; }
  .ft-badge.inactive    { background: #f1f5f9; color: #94a3b8; }
  .ft-badge.optional-on  { background: #fef3c7; color: #92400e; }
  .ft-badge.optional-off { background: #f1f5f9; color: #94a3b8; }
  .ft-badge:hover { filter: brightness(0.93); }
  .ft-action-btn {
    width: 30px; height: 30px; border-radius: 8px; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s; flex-shrink: 0;
  }
  .ft-action-btn:hover:not(:disabled) { transform: scale(1.1); }
  .ft-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ft-action-btn.edit   { background: #eff6ff; color: #2855ae; }
  .ft-action-btn.save   { background: #dcfce7; color: #15803d; }
  .ft-action-btn.cancel { background: #f1f5f9; color: #64748b; }
  .ft-action-btn.delete { background: #fee2e2; color: #dc2626; }
  .add-btn {
    width: 100%; padding: 12px; border-radius: 12px;
    border: 2px dashed #cbd5e1; background: white;
    color: #64748b; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    font-family: 'DM Sans', sans-serif;
  }
  .add-btn:hover { border-color: #3b82f6; color: #2855ae; background: #f0f7ff; }
  .main-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 20px; border: none; border-radius: 11px;
    font-weight: 700; font-size: 13px; cursor: pointer;
    transition: all 0.15s; font-family: 'DM Sans', sans-serif;
  }
  .main-btn:hover:not(:disabled) { transform: translateY(-1px); }
  .main-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none !important; }
  .main-btn.primary { background: linear-gradient(135deg,#1e3a8a,#2563eb); color: white; box-shadow: 0 2px 8px rgba(37,99,235,0.25); }
  .main-btn.success { background: linear-gradient(135deg,#059669,#10b981); color: white; box-shadow: 0 2px 8px rgba(5,150,105,0.2); }
  .main-btn.ghost   { background: white; border: 1.5px solid #e2e8f0; color: #475569; }
  .main-btn.ghost:hover:not(:disabled) { border-color: #93c5fd; color: #1e40af; background: #eff6ff; }
  .stat-chip {
    padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 700;
    display: inline-flex; align-items: center; gap: 5px;
  }
  .bulk-result-item {
    display: flex; align-items: flex-start; gap: 8px;
    padding: 8px 12px; border-radius: 9px; margin-bottom: 4px; font-size: 12px;
  }
  .bulk-result-item.ok  { background: #f0fdf4; border: 1px solid #bbf7d0; }
  .bulk-result-item.err { background: #fff1f2; border: 1px solid #fecdd3; }
  .ft-search {
    width: 100%; padding: 10px 14px 10px 38px;
    border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 13px; outline: none; font-family: 'DM Sans', sans-serif;
    background: #fafbfc; color: #1e293b; transition: all 0.15s;
  }
  .ft-search:focus { border-color: #3b82f6; background: white; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
`;

const uid = () => Math.random().toString(36).slice(2, 9);

export default function FeeTypesSetup() {
  const { feeTypes, loading, fetchFeeTypes } = useFeeTypesStore();
  const { schoolDetails } = useSchoolDetailsStore();
  const schoolId = schoolDetails?.school_id ?? null; // Use school_id instead of session_id

  const [localTypes, setLocalTypes]           = useState<LocalFeeType[]>([]);
  const [saving, setSaving]                   = useState<Record<string, boolean>>({});
  const [search, setSearch]                   = useState('');
  const [filterStatus, setFilterStatus]       = useState<'all' | 'active' | 'inactive'>('all');
  const [bulkUploading, setBulkUploading]     = useState(false);
  const [bulkResults, setBulkResults]         = useState<BulkResult[]>([]);
  const [showBulkResults, setShowBulkResults] = useState(false);
  const [bulkEntries, setBulkEntries]         = useState<BulkEntry[]>([{ id: uid(), fee_name: '', status: 'active' }]);

  const toast         = useRef<Toast>(null);
  const newRowCounter = useRef(0);

  // Fetch once school_id has resolved from the school details store, and re-fetch if it changes.
  useEffect(() => {
    if (schoolId) fetchFeeTypes(schoolId);
  }, [schoolId]);

  useEffect(() => {
    if (feeTypes.length > 0) {
      setLocalTypes(feeTypes.map((ft: any) => ({
        id: ft.id, fee_name: ft.fee_name, status: ft.status,
        optional: !!ft.optional, isEditing: false, isDirty: false, isNew: false,
      })));
    }
  }, [feeTypes]);

  const filtered      = localTypes.filter(ft =>
    ft.fee_name.toLowerCase().includes(search.toLowerCase()) &&
    (filterStatus === 'all' || ft.status === filterStatus)
  );
  const activeCount   = localTypes.filter(f => f.status === 'active' && !f.isNew).length;
  const inactiveCount = localTypes.filter(f => f.status === 'inactive' && !f.isNew).length;
  const dirtyCount    = localTypes.filter(f => f.isDirty || f.isNew).length;

  // Check for school_id instead of session_id
  const requireSchool = () => {
    if (!schoolId) {
      toast.current?.show({ severity: 'error', summary: 'No School', detail: 'School ID could not be resolved.', life: 4000 });
      return false;
    }
    return true;
  };

  const addNewRow = () => {
    newRowCounter.current -= 1;
    setLocalTypes(prev => [...prev, { id: null, fee_name: '', status: 'active', optional: false, isEditing: true, isDirty: true, isNew: true }]);
  };

  const startEdit  = (idx: number) => setLocalTypes(prev => prev.map((ft, i) => i === idx ? { ...ft, isEditing: true } : ft));

  const cancelEdit = (idx: number) => {
    const ft = localTypes[idx];
    if (ft.isNew) { setLocalTypes(prev => prev.filter((_, i) => i !== idx)); return; }
    const original = feeTypes.find((f: any) => f.id === ft.id);
    if (original) setLocalTypes(prev => prev.map((f, i) => i === idx
      ? { ...f, fee_name: original.fee_name, status: original.status, optional: !!original.optional, isEditing: false, isDirty: false }
      : f));
  };

  const updateField = (idx: number, field: keyof LocalFeeType, value: any) =>
    setLocalTypes(prev => prev.map((ft, i) => i === idx ? { ...ft, [field]: value, isDirty: true } : ft));

  const saveRow = async (idx: number) => {
    const ft = localTypes[idx];
    if (!ft.fee_name.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Fee name cannot be empty', life: 3000 });
      return;
    }
    if (!requireSchool()) return;

    const key = ft.id ?? `new-${idx}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      if (ft.isNew) {
        const res = await apiClient.post('/api/fee-types', {
          school_id: schoolId, // Use school_id instead of session_id
          fee_name: ft.fee_name.trim(),
          status: ft.status,
          optional: ft.optional,
        });
        if (res.data.status === 'success') {
          toast.current?.show({ severity: 'success', summary: 'Added', detail: `"${ft.fee_name}" created`, life: 3000 });
          await fetchFeeTypes(schoolId as string);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Create failed', life: 4000 });
        }
      } else {
        const res = await apiClient.put(`/api/fee-types/${ft.id}`, {
          school_id: schoolId, // Use school_id instead of session_id
          fee_name: ft.fee_name.trim(),
          status: ft.status,
          optional: ft.optional,
        });
        if (res.data.status === 'success') {
          toast.current?.show({ severity: 'success', summary: 'Updated', detail: `"${ft.fee_name}" saved`, life: 3000 });
          setLocalTypes(prev => prev.map((f, i) => i === idx ? { ...f, isEditing: false, isDirty: false } : f));
          await fetchFeeTypes(schoolId as string);
        } else {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Update failed', life: 4000 });
        }
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Save failed', life: 4000 });
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const deleteRow = (idx: number) => {
    const ft = localTypes[idx];
    if (ft.isNew) { setLocalTypes(prev => prev.filter((_, i) => i !== idx)); return; }
    if (!requireSchool()) return;
    confirmDialog({
      message: `Delete "${ft.fee_name}"? This cannot be undone.`,
      header: 'Delete Fee Type',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await apiClient.delete(`/api/fee-types/${ft.id}`, {
            data: { school_id: schoolId } // Use school_id instead of session_id
          });
          toast.current?.show({ severity: 'success', summary: 'Deleted', detail: `"${ft.fee_name}" removed`, life: 3000 });
          await fetchFeeTypes(schoolId as string);
        } catch (err: any) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Delete failed', life: 4000 });
        }
      }
    });
  };

  const toggleStatus = async (idx: number) => {
    const ft = localTypes[idx];
    if (ft.isNew || ft.isEditing) { updateField(idx, 'status', ft.status === 'active' ? 'inactive' : 'active'); return; }
    if (!requireSchool()) return;
    const newStatus = ft.status === 'active' ? 'inactive' : 'active';
    setLocalTypes(prev => prev.map((f, i) => i === idx ? { ...f, status: newStatus } : f));
    try {
      await apiClient.put(`/api/fee-types/${ft.id}`, {
        school_id: schoolId, // Use school_id instead of session_id
        status: newStatus
      });
      toast.current?.show({ severity: 'success', summary: 'Updated', detail: `"${ft.fee_name}" set to ${newStatus}`, life: 2000 });
      await fetchFeeTypes(schoolId as string);
    } catch {
      setLocalTypes(prev => prev.map((f, i) => i === idx ? { ...f, status: ft.status } : f));
    }
  };

  const toggleOptional = async (idx: number) => {
    const ft = localTypes[idx];
    if (ft.isNew || ft.isEditing) { updateField(idx, 'optional', !ft.optional); return; }
    if (!requireSchool()) return;
    const newOpt = !ft.optional;
    setLocalTypes(prev => prev.map((f, i) => i === idx ? { ...f, optional: newOpt } : f));
    try {
      await apiClient.put(`/api/fee-types/${ft.id}`, {
        school_id: schoolId, // Use school_id instead of session_id
        optional: newOpt
      });
      await fetchFeeTypes(schoolId as string);
    } catch {
      setLocalTypes(prev => prev.map((f, i) => i === idx ? { ...f, optional: ft.optional } : f));
    }
  };

  const addBulkEntry    = () => setBulkEntries(prev => [...prev, { id: uid(), fee_name: '', status: 'active' }]);
  const removeBulkEntry = (id: string) => setBulkEntries(prev => prev.length > 1 ? prev.filter(e => e.id !== id) : prev);
  const updateBulkEntry = (id: string, field: keyof Omit<BulkEntry, 'id'>, value: string) =>
    setBulkEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

  const handleBulkSubmit = async () => {
    const valid = bulkEntries.filter(e => e.fee_name.trim());
    if (valid.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Empty', detail: 'Add at least one fee type name', life: 3000 });
      return;
    }
    if (!requireSchool()) return;

    setBulkUploading(true);
    setBulkResults([]);
    try {
      const res = await apiClient.post('/api/fee-types/bulk', {
        school_id: schoolId, // Use school_id instead of session_id
        fee_types: valid.map(e => ({ fee_name: e.fee_name.trim(), status: e.status })),
      });
      const rawResults: BulkResult[] = res.data.results || [];
      setBulkResults(rawResults);
      setShowBulkResults(true);
      const ok  = rawResults.filter(isSuccess).length;
      const err = rawResults.filter(isError).length;
      toast.current?.show({
        severity: ok > 0 && err === 0 ? 'success' : ok > 0 ? 'warn' : 'error',
        summary: ok > 0 && err === 0 ? 'Bulk Import Complete' : 'Partial Import',
        detail: `${ok} added${err > 0 ? `, ${err} failed` : ''}`,
        life: 5000,
      });
      if (ok > 0) { setBulkEntries([{ id: uid(), fee_name: '', status: 'active' }]); await fetchFeeTypes(schoolId as string); }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Import Failed', detail: err.response?.data?.message || 'Bulk import failed', life: 4000 });
    } finally {
      setBulkUploading(false);
    }
  };

  const isSuccess = (r: BulkResult) => (r.status || '').toLowerCase() === 'success';
  const isError   = (r: BulkResult) => (r.status || '').toLowerCase() === 'error';
  const bulkSuccessCount = bulkResults.filter(isSuccess).length;
  const bulkErrorCount   = bulkResults.filter(isError).length;

  return (
    <div className="fts-root">
      <style>{GS}</style>
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* ── Top action bar ── */}
      <div style={{
        background: 'white', borderRadius: 16, padding: '14px 20px', marginBottom: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1.5px solid #e8edf5',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="stat-chip" style={{ background: '#dcfce7', color: '#15803d' }}>
            <IcoCheckCircle size={12} /> {activeCount} Active
          </span>
          <span className="stat-chip" style={{ background: '#f1f5f9', color: '#64748b' }}>
            <IcoMinusCircle size={12} /> {inactiveCount} Inactive
          </span>
          {dirtyCount > 0 && (
            <span className="stat-chip" style={{ background: '#fef3c7', color: '#92400e', animation: 'pulse 2s infinite' }}>
              <IcoWarn size={12} /> {dirtyCount} Unsaved
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="main-btn ghost" onClick={() => schoolId && fetchFeeTypes(schoolId)} disabled={loading || !schoolId}>
            {loading ? <IcoSpinner size={14} /> : <IcoRefresh size={14} />}
            {loading ? 'Loading...' : 'Reload'}
          </button>
          <button className="main-btn primary" onClick={addNewRow}>
            <IcoPlus size={14} /> Add Fee Type
          </button>
        </div>
      </div>

      {/* ── Fee types list ── */}
      <div className="fts-card">
        <div className="fts-card-title">
          <div className="fts-icon"><IcoList size={14} style={{ color: 'white' }} /></div>
          Fee Types
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex', borderRadius: 9, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
              {(['all', 'active', 'inactive'] as const).map(f => (
                <button key={f} onClick={() => setFilterStatus(f)} style={{
                  padding: '5px 12px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: filterStatus === f ? '#1e3a8a' : 'white',
                  color: filterStatus === f ? 'white' : '#64748b',
                  textTransform: 'capitalize', transition: 'all 0.15s',
                }}>{f}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <IcoSearch size={13} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input className="ft-search" placeholder="Search fee types..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px', marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Fee Name</span>
          <span style={{ width: 80, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>Status</span>
          <span style={{ width: 80, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center' }}>Optional</span>
          <span style={{ width: 96, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Actions</span>
        </div>

        {/* Rows */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <IcoSpinner size={32} style={{ color: '#2855ae', display: 'block', margin: '0 auto' }} />
            <p style={{ marginTop: 12, fontSize: 14, color: '#64748b' }}>Loading fee types...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <IcoInbox size={40} style={{ color: '#cbd5e1', display: 'block', margin: '0 auto 12px' }} />
            <p style={{ color: '#64748b', fontWeight: 600, margin: 0 }}>
              {search ? `No results for "${search}"` : 'No fee types yet. Add one below.'}
            </p>
          </div>
        ) : (
          filtered.map(ft => {
            const realIdx  = localTypes.indexOf(ft);
            const isSaving = !!saving[ft.id ?? `new-${realIdx}`];
            return (
              <div key={ft.id ?? `new-${realIdx}`}
                className={`ft-row${ft.isEditing ? ' editing' : ''}${ft.isDirty && !ft.isNew ? ' dirty' : ''}${ft.isNew ? ' new-row' : ''}${ft.status === 'inactive' && !ft.isEditing ? ' inactive-row' : ''}`}
              >
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {ft.isNew && <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>NEW</span>}
                  <input
                    className="ft-name-input"
                    value={ft.fee_name}
                    readOnly={!ft.isEditing}
                    placeholder="Enter fee type name..."
                    onChange={e => updateField(realIdx, 'fee_name', e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveRow(realIdx); if (e.key === 'Escape') cancelEdit(realIdx); }}
                    autoFocus={ft.isNew}
                    maxLength={50}
                    style={{ fontStyle: ft.fee_name ? 'normal' : 'italic' }}
                  />
                  {ft.isEditing && ft.fee_name.length > 35 && (
                    <span style={{ fontSize: 10, color: ft.fee_name.length >= 50 ? '#dc2626' : '#f59e0b', whiteSpace: 'nowrap' }}>{ft.fee_name.length}/50</span>
                  )}
                </div>

                <div style={{ width: 80, textAlign: 'center' }}>
                  <span className={`ft-badge ${ft.status}`} onClick={() => toggleStatus(realIdx)} title="Click to toggle">
                    {ft.status === 'active' ? <IcoCheckCircle size={10} /> : <IcoMinusCircle size={10} />}
                    {ft.status}
                  </span>
                </div>

                <div style={{ width: 80, textAlign: 'center' }}>
                  <span className={`ft-badge ${ft.optional ? 'optional-on' : 'optional-off'}`} onClick={() => toggleOptional(realIdx)} title="Click to toggle">
                    {ft.optional ? <IcoStarFill size={10} /> : <IcoStar size={10} />}
                    {ft.optional ? 'Yes' : 'No'}
                  </span>
                </div>

                <div style={{ width: 96, display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                  {ft.isEditing ? (
                    <>
                      <button className="ft-action-btn save" onClick={() => saveRow(realIdx)} disabled={isSaving} title="Save (Enter)">
                        {isSaving ? <IcoSpinner size={12} /> : <IcoCheck size={12} />}
                      </button>
                      <button className="ft-action-btn cancel" onClick={() => cancelEdit(realIdx)} title="Cancel (Esc)">
                        <IcoX size={12} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="ft-action-btn edit" onClick={() => startEdit(realIdx)} title="Edit">
                        <IcoPencil size={12} />
                      </button>
                      <button className="ft-action-btn delete" onClick={() => deleteRow(realIdx)} title="Delete">
                        <IcoTrash size={12} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}

        <div style={{ marginTop: 12 }}>
          <button className="add-btn" onClick={addNewRow}>
            <IcoPlus size={13} /> Add New Fee Type
          </button>
        </div>
      </div>

      {/* ── Bulk Import ── */}
      <div className="fts-card">
        <div className="fts-card-title">
          <div className="fts-icon" style={{ background: 'linear-gradient(135deg,#059669,#10b981)' }}>
            <IcoList size={14} style={{ color: 'white' }} />
          </div>
          Bulk Import
          <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>Add multiple fee types at once</span>
          <button className="main-btn ghost" style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: 12 }} onClick={addBulkEntry}>
            <IcoPlus size={11} /> Add Row
          </button>
        </div>

        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#475569', display: 'flex', gap: 10, alignItems: 'center' }}>
          <IcoInfo size={14} style={{ color: '#3b82f6', flexShrink: 0 }} />
          <span>Fill in fee type names below. Status defaults to <code style={{ background: '#e2e8f0', padding: '1px 5px', borderRadius: 4 }}>active</code> if not changed. Max 50 chars. Duplicates are skipped.</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px', marginBottom: 8 }}>
          <span style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Fee Name</span>
          <span style={{ width: 110, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Status</span>
          <span style={{ width: 32 }} />
        </div>

        <div style={{ marginBottom: 14 }}>
          {bulkEntries.map((entry, idx) => (
            <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, marginBottom: 6, border: '1.5px solid #e8edf5', background: '#fafbfc' }}>
              <span style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 700, minWidth: 20, textAlign: 'right' }}>{idx + 1}</span>
              <input
                style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, color: '#1e293b', outline: 'none', fontFamily: 'DM Sans, sans-serif' }}
                placeholder="Enter fee type name..."
                value={entry.fee_name}
                maxLength={50}
                onChange={e => updateBulkEntry(entry.id, 'fee_name', e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addBulkEntry(); }}
              />
              {entry.fee_name.length > 35 && (
                <span style={{ fontSize: 10, color: entry.fee_name.length >= 50 ? '#dc2626' : '#f59e0b', whiteSpace: 'nowrap' }}>{entry.fee_name.length}/50</span>
              )}
              <select value={entry.status} onChange={e => updateBulkEntry(entry.id, 'status', e.target.value)} style={{
                width: 110, border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 700,
                color: entry.status === 'active' ? '#15803d' : '#94a3b8',
                background: entry.status === 'active' ? '#dcfce7' : '#f1f5f9',
                cursor: 'pointer', outline: 'none', fontFamily: 'DM Sans, sans-serif',
              }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button onClick={() => removeBulkEntry(entry.id)} disabled={bulkEntries.length === 1} style={{
                width: 28, height: 28, borderRadius: 7, border: 'none', background: '#fee2e2', color: '#dc2626',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: bulkEntries.length === 1 ? 0.3 : 1,
              }}>
                <IcoTrash size={11} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="add-btn" style={{ flex: 1 }} onClick={addBulkEntry}>
            <IcoPlus size={13} /> Add Another Row
          </button>
          <button className="main-btn success" onClick={handleBulkSubmit} disabled={bulkUploading || bulkEntries.every(e => !e.fee_name.trim())}>
            {bulkUploading
              ? <><IcoSpinner size={14} /> Importing...</>
              : <><IcoUpload size={14} /> Import{bulkEntries.filter(e => e.fee_name.trim()).length > 0 ? ` (${bulkEntries.filter(e => e.fee_name.trim()).length})` : ''}</>
            }
          </button>
        </div>

        {showBulkResults && bulkResults.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Import Results</span>
              <span style={{ background: '#dcfce7', color: '#15803d', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>{bulkSuccessCount} added</span>
              {bulkErrorCount > 0 && <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6 }}>{bulkErrorCount} errors</span>}
              <button onClick={() => setShowBulkResults(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 4 }}>
                <IcoX size={13} />
              </button>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {bulkResults.map((r, i) => (
                <div key={i} className={`bulk-result-item ${isSuccess(r) ? 'ok' : 'err'}`}>
                  {isSuccess(r)
                    ? <IcoCheckCircle size={13} style={{ color: '#15803d', flexShrink: 0, marginTop: 1 }} />
                    : <IcoWarn size={13} style={{ color: '#be123c', flexShrink: 0, marginTop: 1 }} />
                  }
                  <div>
                    {r.fee_name && <strong style={{ color: isSuccess(r) ? '#15803d' : '#be123c' }}>{r.fee_name}</strong>}
                    <span style={{ color: '#64748b', marginLeft: r.fee_name ? 6 : 0 }}>— {r.message}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}